import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { Box, Link as MuiLink, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  isBillingRestrictedNavPath,
  privateNavPageTitleKey,
} from '../../../helpers/privateNavPageTitle';
import { canPerformAction, PERMISSIONS } from '../../../utils/permissions';
import UsageQuotaTopBar from './UsageQuotaTopBar';
import UserAvatarMenu from './UserAvatarMenu';

/**
 * Authenticated desktop top strip: soft background, optional back link + page title,
 * right cluster (plan + usage) in a white pill — aligned with product reference layouts.
 */
const PrivateAppTopBar: React.FC = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const canManageBilling = Boolean(
    currentUser && canPerformAction(currentUser, PERMISSIONS.MANAGE_BILLING)
  );

  const hideBillingNavChrome =
    isBillingRestrictedNavPath(location.pathname) && !canManageBilling;
  const titleKey = hideBillingNavChrome
    ? 'Dashboard'
    : privateNavPageTitleKey(location.pathname);
  const showBackLink =
    location.pathname !== '/dashboard' && !hideBillingNavChrome;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        px: { xs: 2, md: 3 },
        py: { xs: 1.75, md: 2 },
        bgcolor: '#F6F8FC',
      }}
    >
      <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
        {showBackLink && (
          <MuiLink
            component={Link}
            to="/dashboard"
            underline="none"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.15,
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: '#94A3B8',
              letterSpacing: 0.01,
              mb: 0.35,
              '&:hover': { color: '#64748B' },
            }}
          >
            <ChevronLeft sx={{ fontSize: '1.1rem', ml: -0.5 }} aria-hidden />
            {t('Back to dashboard')}
          </MuiLink>
        )}
        <Typography
          component="h1"
          sx={{
            m: 0,
            fontSize: { xs: '1.35rem', md: '1.6rem' },
            fontWeight: 700,
            color: '#0F172A',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}
        >
          {t(titleKey)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          flexShrink: 0,
        }}
      >
        {canManageBilling && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              bgcolor: '#FFFFFF',
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0px 4px 14px rgba(15, 23, 42, 0.08)',
            }}
          >
            <UsageQuotaTopBar />
          </Box>
        )}
        <UserAvatarMenu />
      </Box>
    </Box>
  );
};

export default PrivateAppTopBar;
